import { Injectable, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject } from 'rxjs';
import {
    ALL_DENIED_COOKIE_PREFERENCES,
    ALL_GRANTED_COOKIE_PREFERENCES,
    COOKIE_CONSENT_STATUS_KEY,
    COOKIES_PREFERENCES_STORAGE_KEY,
    CookiePreferences,
} from './cookie-preferences.model';

type ConsentState = 'granted' | 'denied';

@Injectable({
    providedIn: 'root',
})
export class GoogleConsentService {
    private readonly cookieService = inject(CookieService);
    private readonly preferencesUpdatedSubject = new Subject<CookiePreferences>();
    private currentPreferences: CookiePreferences = { ...ALL_DENIED_COOKIE_PREFERENCES };

    readonly preferencesUpdated$: Observable<CookiePreferences> =
        this.preferencesUpdatedSubject.asObservable();

    applyDefaultConsent(): void {
        this.invokeGtag('consent', 'default', {
            ad_storage: 'denied',
            analytics_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            functionality_storage: 'denied',
            wait_for_update: 500,
        });
        this.invokeGtag('set', 'ads_data_redaction', true);
        this.currentPreferences = { ...ALL_DENIED_COOKIE_PREFERENCES };
    }

    updateFromPreferences(preferences: CookiePreferences): void {
        const normalized = this.normalizePreferences(preferences);
        this.currentPreferences = normalized;

        this.invokeGtag('consent', 'update', {
            analytics_storage: this.toConsentState(normalized.analysis),
            ad_storage: this.toConsentState(normalized.advertising),
            ad_user_data: this.toConsentState(normalized.advertising2),
            ad_personalization: this.toConsentState(normalized.advertising2),
            functionality_storage: this.toConsentState(normalized.functionality),
        });

        this.invokeGtag('set', 'ads_data_redaction', !normalized.advertising);
        this.preferencesUpdatedSubject.next(normalized);
    }

    restoreFromStorage(): boolean {
        const consentStatus = this.cookieService.get(COOKIE_CONSENT_STATUS_KEY);
        if (!consentStatus) {
            return false;
        }

        const preferences = this.resolvePreferences(consentStatus);
        this.updateFromPreferences(preferences);
        return true;
    }

    getPreferences(): CookiePreferences {
        return { ...this.currentPreferences };
    }

    readStoredPreferences(): CookiePreferences | null {
        const raw = localStorage.getItem(COOKIES_PREFERENCES_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        try {
            return this.normalizePreferences(JSON.parse(raw) as CookiePreferences);
        } catch {
            return null;
        }
    }

    hasAnalyticsConsent(): boolean {
        return !!this.currentPreferences.analysis;
    }

    hasAdvertisingConsent(): boolean {
        return !!this.currentPreferences.advertising || !!this.currentPreferences.advertising2;
    }

    hasFunctionalityConsent(): boolean {
        return !!this.currentPreferences.functionality;
    }

    private resolvePreferences(consentStatus: string): CookiePreferences {
        if (consentStatus === 'allow') {
            return { ...ALL_GRANTED_COOKIE_PREFERENCES };
        }

        if (consentStatus === 'deny') {
            return { ...ALL_DENIED_COOKIE_PREFERENCES };
        }

        return this.readStoredPreferences() ?? { ...ALL_DENIED_COOKIE_PREFERENCES };
    }

    private normalizePreferences(preferences: CookiePreferences): CookiePreferences {
        return {
            analysis: !!preferences.analysis,
            functionality: !!preferences.functionality,
            advertising: !!preferences.advertising,
            advertising2: !!preferences.advertising2,
        };
    }

    private toConsentState(value: boolean | undefined): ConsentState {
        return value ? 'granted' : 'denied';
    }

    private invokeGtag(...args: unknown[]): void {
        window.dataLayer = window.dataLayer || [];

        if (typeof window.gtag === 'function') {
            window.gtag(...args);
            return;
        }

        window.dataLayer.push(args);
    }
}
